import { useQuery } from '@tanstack/react-query';
import { Button, Spinner } from '@/components/ui';
import { getCompanyEmployment } from '@/api/services/employment.service';
import { getErrorMessage } from '@/lib/getErrorMessage';

function formatInstant(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

/**
 * Applied assignment snapshots, newest first. Each row is immutable history;
 * end times are exclusive and derived from the next snapshot's start.
 */
export function CompanyEmploymentHistory({
  companyId,
  userId,
  at,
}: {
  companyId: string;
  userId: string;
  at?: string;
}): React.ReactElement {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['hr-employment-detail', companyId, userId, at],
    queryFn: () => getCompanyEmployment(companyId, userId, at),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-10" role="status" aria-live="polite">
        <Spinner />
        <span className="sr-only">Loading assignment history</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-3" role="alert">
        <p className="text-sm text-danger">{getErrorMessage(error)}</p>
        <Button variant="secondary" size="sm" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  if (!data) return <></>;

  return (
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
        Assignment history
      </h3>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
        Each entry is an applied snapshot. End times are exclusive.
      </p>
      <ol className="mt-3 divide-y divide-gray-200 rounded-xl border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
        {data.assignments.map((assignment) => (
          <li key={assignment._id} className="space-y-3 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {assignment.jobTitleName ?? assignment.positionName ?? 'Assignment'}
              </p>
              <span className="text-xs capitalize text-gray-600 dark:text-gray-300">
                {assignment.source.replaceAll('_', ' ')}
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-300">
              {formatInstant(assignment.effectiveFrom)} →{' '}
              {assignment.effectiveTo ? formatInstant(assignment.effectiveTo) : 'Ongoing'}
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-200">
              {[assignment.departmentName, assignment.teamName, assignment.employmentTypeName]
                .filter(Boolean)
                .join(' · ') || 'No structure details recorded'}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}
